import { z } from 'zod';

/**
 * Roles and permissions.
 *
 * The authoritative role -> permission binding lives in the WorkOS dashboard and
 * is delivered to the API inside the access token (`role` + `permissions` claims).
 * The catalog below is the shared, typed contract used by the API to gate actions
 * and by the frontend to show/hide UI. `ROLE_PERMISSIONS` mirrors the WorkOS
 * configuration for reference/tests only — it is not the runtime source of truth.
 */

export const RoleSchema = z.enum(['admin', 'user', 'agent', 'reviewer']);
export type Role = z.infer<typeof RoleSchema>;

export const PERMISSIONS = {
  PROPERTIES_READ: 'properties:read',
  PROPERTIES_WRITE: 'properties:write',
  PROPERTIES_CREATE: 'properties:create',
  TASKS_READ: 'tasks:read',
  TASKS_ASSIGN: 'tasks:assign',
  ROLES_ASSIGN: 'roles:assign',
  REGIONS_UPDATE: 'regions:update',
  AUDIT_READ: 'audit:read',
  INSPECTIONS_READ: 'inspections:read',
  INSPECTIONS_SUBMIT: 'inspections:submit',
  INSPECTIONS_REVIEW: 'inspections:review',
  MEDIA_WRITE: 'media:write',
  FEEDBACK_READ: 'feedback:read',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/**
 * Reference mapping of role -> permissions. Keep in sync with the WorkOS
 * dashboard. `reviewer` is defined ahead of the workflow being built so the
 * role can be enabled with no code changes.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ALL_PERMISSIONS.filter((p) => p !== PERMISSIONS.PROPERTIES_CREATE),
  user: [
    PERMISSIONS.PROPERTIES_READ,
    PERMISSIONS.PROPERTIES_WRITE,
    PERMISSIONS.PROPERTIES_CREATE,
    PERMISSIONS.INSPECTIONS_READ,
  ],
  agent: [
    PERMISSIONS.TASKS_READ,
    PERMISSIONS.INSPECTIONS_READ,
    PERMISSIONS.INSPECTIONS_SUBMIT,
    PERMISSIONS.MEDIA_WRITE,
  ],
  reviewer: [PERMISSIONS.INSPECTIONS_READ, PERMISSIONS.INSPECTIONS_REVIEW],
};

/**
 * Authentication context passed to service methods.
 *
 * `role` and `permissions` are read directly from the verified WorkOS access
 * token. `permissions` defaults to an empty array when the token predates the
 * WorkOS Roles & Permissions configuration, which fails safe (authenticated but
 * unauthorized for any gated action).
 */
export interface AuthContext {
  userId: string;
  email: string;
  role?: Role;
  permissions: Permission[];
  organizationId?: string;
}
